import React, { useState, useEffect } from "react";
import { Row, Col, Card, Typography, Spin, message, Button, Space } from "antd";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import dashboardService from "../../services/dashboardService";
import cacheService from "../../services/cacheService";
import ChartCard from "../../components/cards/ChartCard";
import MiniBar from "../../components/charts/MiniBar";
import { useCommon } from "../../context/CommonContext";

const { Title, Text } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [clearingCache, setClearingCache] = useState(false);
  // Only the show count is displayed now; getAllCounts still returns the rest in
  // the same single request, and setCounts replaces this object wholesale.
  const [counts, setCounts] = useState({ shows: 0 });
  const { setPageTitle } = useCommon();

  useEffect(() => {
    setPageTitle("Dashboard");
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const dashboardCounts = await dashboardService.getAllCounts();
      setCounts(dashboardCounts);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      message.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleClearRedisCache = async () => {
    try {
      setClearingCache(true);
      const response = await cacheService.clearCache();
      message.success(response?.data?.message || "Redis cache cleared successfully");
    } catch (error) {
      console.error("Error clearing Redis cache:", error);
    } finally {
      setClearingCache(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="page-header">
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col>
            <Title level={2}>Admin Dashboard</Title>
            <Text type="secondary">
              Welcome back, {user?.name || "Admin"}! Here's what's happening with
              your OTT platform.
            </Text>
          </Col>
          <Col>
            <Space>
              <Button
                danger
                type="primary"
                loading={clearingCache}
                onClick={handleClearRedisCache}
              >
                Clear Redis Cache
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} lg={6}>
          <ChartCard
            title="Total TV Shows"
            total={counts.shows.toLocaleString()}
            action
          >
            <MiniBar color="#52c41a" />
          </ChartCard>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row gutter={[16, 16]} style={{ marginTop: "24px" }}>
        <Col xs={24}>
          <Card title="Quick Actions">
            <Row gutter={[16, 16]}>
              {[
                { label: "Banners", path: "/banners" },
                { label: "Series", path: "/series" },
                { label: "Collections", path: "/collections" },
              ].map((action) => (
                <Col xs={24} sm={8} key={action.path}>
                  <Card
                    hoverable
                    style={{ textAlign: "center" }}
                    onClick={() => navigate(action.path)}
                  >
                    <Text strong>{action.label}</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
